import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { EMAIL_MESSAGES } from './email.js';
import { PageMetaShape, PickerIdsSchema, pagedQuery, pagedResponse } from './pagination.js';
import { AuthorshipSchema, CurrencySchema, IsoDateSchema, MessageSchema, MoneyStringSchema, PctStringSchema, } from './primitives.js';
const c = initContract();
/**
 * Business Partners (PART 5, MD-01) — the shared partner record: ONE row per
 * company traded with, customer and/or supplier via the types pair.
 *
 * Permission area: `crm` — PART 16.2.2 row 5 governs "Customers and
 * suppliers, statements, leads, opportunities, follow-ups", and the matrix
 * agrees (the AP Clerk "can read the partner list" = crm R, while holding
 * nothing on sales). Every route is annotated crm.* accordingly.
 *
 * Shapes only; rules live in PartnersService:
 *  - BR-MD-01-03: duplicate tax id refused (409, the spec's sentence).
 *  - Duplicate NAME warns and then saves — the `warnings` array on 201/200.
 *  - BR-MD-01-07/-12: switch-off is a route, not a delete; refused while an
 *    unpaid POSTED invoice exists (drafts never block — MD-RULE-7).
 *  - MD-RULE-1/BR-MD-01-09: delete refused once used anywhere (RESTRICT FKs
 *    are the structural floor).
 *  - Balances and the statement count POSTED documents only (R9).
 *
 * No tenant id anywhere, as always: the session carries it (§5.7).
 */
// ── The fixed sentences of MD-01 ────────────────────────────────────────────
export const PARTNER_MESSAGES = {
    /** BR-MD-01-01 / ERR-MD-01-01. */
    nameRequired: "Type the customer's name.",
    /** ERR-MD-01-03. */
    emailInvalid: 'Type a full email address, like name@company.com.',
    /**
     * BR-MD-01-03 / ERR-MD-01-02, and ERR-MD-02-02 verbatim from the other side
     * ("V-0031 Acme Trading Co. already uses that tax number.") — the same
     * sentence for both, because there is one partner table and one tax-number
     * index, so a supplier cannot take a customer's number either.
     */
    taxIdTaken: (code, name) => `${code} ${name} already uses that tax number.`,
    /**
     * ERR-MD-01-05 (MD-RULE-1), and ERR-MD-02-04 word for word: "Acme Raw
     * Materials is used on 12 documents and cannot be deleted. Switch it off
     * instead." One sentence, both sides — the delete refusal does not depend
     * on which direction the documents point.
     */
    inUse: (name, documents) => `${name} is used on ${documents} ${documents === 1 ? 'document' : 'documents'} and cannot be deleted. Switch it off instead.`,
    /** BR-MD-01-12 / ERR-MD-01-06 — drafts never block the switch-off. */
    owesOnSwitchOff: (name, amount) => `${name} owes ${amount}. Settle or write off the balance before switching them off.`,
    /**
     * BR-MD-02-10 / ERR-MD-02-05, verbatim: "You owe Acme Raw Materials 900.00.
     * Pay or write off the balance before switching them off."
     *
     * The SAME rule as BR-MD-01-12 in the other direction, and it needs its own
     * sentence rather than a shared one because the debt points the other way.
     * `owesOnSwitchOff` reads "Acme owes 900.00", which is false about a
     * supplier and is exactly the confusion MD-02 §1a's table exists to prevent
     * ("Where the money sits: owed to us / we owe"). A partner with both types
     * ticked can hit either, and the two figures are "never netted off".
     */
    weOweOnSwitchOff: (name, amount) => `You owe ${name} ${amount}. Pay or write off the balance before switching them off.`,
    /** BR-MD-01-05b (customer half; the supplier half arrives with purchasing). */
    mustStayCustomer: (name, documents) => `${name} has ${documents} sales ${documents === 1 ? 'document' : 'documents'}. It must stay a customer.`,
    /** BR-MD-01-05 / ERR-MD-01-04. */
    currencyLocked: (name, documents, currency, wanted) => `${name} already has ${documents} ${documents === 1 ? 'document' : 'documents'} in ${currency}. Create a second partner record for ${wanted} trading.`,
    /** BR-MD-01-10a. */
    discountTooHigh: (maxPct = '15') => `The most you can give is ${maxPct}%.`,
    /** ERR-MD-01-08 (also a DB CHECK). */
    negativeCreditLimit: 'A credit limit cannot be below zero. Use 0.00 for no limit.',
    /** BR-MD-01-07 — no spec-fixed wording; one sentence, used everywhere. */
    inactive: (name) => `${name} is switched off and cannot be picked on a new document.`,
    /** The duplicate-name warning ("warns and then saves"). */
    duplicateName: (name) => `A partner named ${name} already exists. Saved anyway — check it is not the same company.`,
    /**
     * CRM-04 §5's two "Send Statement" sentences. They live in EMAIL_MESSAGES,
     * where the rest of the mail vocabulary is, and are re-exported here so a
     * partner screen does not have to know that sending a statement is an email
     * concern.
     */
    statementEmailed: EMAIL_MESSAGES.statementEmailed,
    partnerHasNoEmail: EMAIL_MESSAGES.partnerHasNoEmail,
    /**
     * F-27 on CRM-04, in the sentence shape `FX_MESSAGES.noRateFor` set for
     * ERR-DSH-04-04: a figure that cannot join the total is EXCLUDED and the
     * currency is named, never silently added. CRM-04 §15 is the rule it
     * protects — "the statement is in the partner's currency throughout, with
     * no conversion to the base currency anywhere. There is no combined
     * multi-currency view." Since documents carry their own currency (owner
     * decision, 30 Oct 2026) each currency has its own statement, so the
     * second code is the STATEMENT's currency.
     */
    notThisCurrency: (found, statementCurrency) => `A document in ${found} is on this account; this statement is in ${statementCurrency}. Figures in that currency are not included.`,
    /**
     * INV-F3 — the partner's BRANCHES (see `PartnerBranchSchema`). Two fixed
     * sentences, no values, so each maps to one dictionary key by exact match
     * (`lib/server-messages.ts`) and needs no structured family.
     */
    branchNameTaken: 'A branch with this name already exists for this partner.',
    /** Raised by the sales header when a branch id names another partner's site. */
    branchNotOfPartner: 'That branch belongs to another partner. Pick one of this customer’s branches.',
};
/**
 * D46 — the partner family's structured refusals: key + params beside the
 * composed English sentence, so a refusal that names a partner, a count or an
 * amount reaches an Arabic reader in Arabic. One builder per parameterised
 * member the API raises inside an HTTP exception body — the raise sites throw
 * THESE, and each `message` is composed by the `PARTNER_MESSAGES` twin so the
 * two cannot drift (parity pinned member by member in
 * `apps/api/test/refusal-census.spec.ts`; both-language renders in
 * `apps/web/src/test/refusal-wire-render.test.ts`).
 *
 * `duplicateName` and `notThisCurrency` are deliberately NOT here: neither is
 * an HTTP refusal body — the first rides the 201/200 `warnings` array, the
 * second a statement's `excludedCurrencies` field (both recorded in the
 * census's NOT_REFUSAL_BODIES roster).
 *
 * `partnerHasNoEmail` IS here even though its sentence lives in
 * `EMAIL_MESSAGES`: the alias above is how the API raises it (the partner
 * screen's Send Statement door), so the builder sits with the family that
 * owns the raise site, and its twin for parity purposes is the alias member.
 *
 * The count-bearing members (inUse, mustStayCustomer, currencyLocked) travel
 * the RAW count; the dictionaries pluralise the noun (en mirrors each
 * sentence's own one/other ternary, ar carries the full six-category set).
 * The two amounts (owesOnSwitchOff, weOweOnSwitchOff) travel PRE-FORMATTED by
 * the raise site's own formatter (`withCommas(….toFixed2())`), exactly as
 * `PO_REFUSALS.approvalRequired` does — so dictionary and sentence cannot
 * disagree on grouping. `discountTooHigh` keeps its twin's default so the two
 * signatures stay interchangeable at the raise site.
 */
export const PARTNER_REFUSALS = {
    taxIdTaken: (code, name) => ({
        message: PARTNER_MESSAGES.taxIdTaken(code, name),
        key: 'partners.errTaxIdTaken',
        params: { code, name },
    }),
    inUse: (name, documents) => ({
        message: PARTNER_MESSAGES.inUse(name, documents),
        key: 'partners.errInUse',
        params: { name, documents },
    }),
    owesOnSwitchOff: (name, amount) => ({
        message: PARTNER_MESSAGES.owesOnSwitchOff(name, amount),
        key: 'partners.errOwesOnSwitchOff',
        params: { name, amount },
    }),
    weOweOnSwitchOff: (name, amount) => ({
        message: PARTNER_MESSAGES.weOweOnSwitchOff(name, amount),
        key: 'partners.errWeOweOnSwitchOff',
        params: { name, amount },
    }),
    mustStayCustomer: (name, documents) => ({
        message: PARTNER_MESSAGES.mustStayCustomer(name, documents),
        key: 'partners.errMustStayCustomer',
        params: { name, documents },
    }),
    currencyLocked: (name, documents, currency, wanted) => ({
        message: PARTNER_MESSAGES.currencyLocked(name, documents, currency, wanted),
        key: 'partners.errCurrencyLocked',
        params: { name, documents, currency, wanted },
    }),
    discountTooHigh: (maxPct = '15') => ({
        message: PARTNER_MESSAGES.discountTooHigh(maxPct),
        key: 'partners.errDiscountTooHigh',
        params: { maxPct },
    }),
    inactive: (name) => ({
        message: PARTNER_MESSAGES.inactive(name),
        key: 'partners.errInactive',
        params: { name },
    }),
    partnerHasNoEmail: (name) => ({
        message: PARTNER_MESSAGES.partnerHasNoEmail(name),
        key: 'partners.errHasNoEmail',
        params: { name },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
const NameSchema = z.string().trim().min(1, PARTNER_MESSAGES.nameRequired).max(120);
const EmailSchema = z
    .string()
    .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, PARTNER_MESSAGES.emailInvalid)
    .max(120);
const CountrySchema = z.string().regex(/^[A-Z]{2}$/);
export const CreditEnforcementSchema = z.enum(['block', 'warn', 'none']);
/** The worst-first status chip of MD-01 §7, derived from POSTED documents only. */
export const PartnerStatusSchema = z.enum(['over_limit', 'past_due', 'active', 'inactive']);
/**
 * One currency's balance with a partner (owner decision, 30 Oct 2026: every
 * sales and purchasing document may pick its own currency, and a partner's
 * balances are shown PER CURRENCY — "owes $500 and ₪2,000"). The two figures
 * are never netted across currencies and never converted: each is in
 * `currency`, by the same ledger rule as the partner's scalar `owedToUs` /
 * `owedByUs`. The partner's own (default) currency row includes the opening
 * balance, which is kept in that currency.
 */
export const PartnerCurrencyBalanceSchema = z
    .object({
    currency: CurrencySchema,
    owedToUs: z.string(),
    owedByUs: z.string(),
})
    .strict();
export const PartnerSchema = z
    .object({
    id: z.uuid(),
    /** BP-#### — presentation of partnerNo; never changes (BR-MD-01-04). */
    code: z.string(),
    partnerNo: z.number().int().positive(),
    name: z.string(),
    isCustomer: z.boolean(),
    isSupplier: z.boolean(),
    /** MD-01 field 4 — the group this partner is in (MD-RULE-9's list). */
    partnerGroupId: z.uuid().nullable(),
    /** The group's current name, joined for display (a group is one record). */
    partnerGroupName: z.string().nullable(),
    taxId: z.string().nullable(),
    primaryContact: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    isActive: z.boolean(),
    billingStreet: z.string().nullable(),
    billingCity: z.string().nullable(),
    billingRegion: z.string().nullable(),
    billingPostalCode: z.string().nullable(),
    billingCountry: z.string().nullable(),
    shippingSameAsBilling: z.boolean(),
    shippingStreet: z.string().nullable(),
    shippingCity: z.string().nullable(),
    shippingRegion: z.string().nullable(),
    shippingPostalCode: z.string().nullable(),
    shippingCountry: z.string().nullable(),
    currency: z.string(),
    /** MD-01 field 22 — the MD-09 term this partner is on; null = due on issue. */
    paymentTermId: z.uuid().nullable(),
    /** The term's current name, joined for display (a term is one record). */
    paymentTermName: z.string().nullable(),
    /** null = the company Debt limit default; "0.00" = no limit. */
    creditLimit: z.string().nullable(),
    creditEnforcement: CreditEnforcementSchema,
    discountPct: z.string(),
    /** MD-01 field 26 — the MD-14 tier, by id (BR-MD-14-05: never the text). */
    priceTierId: z.uuid().nullable(),
    /** The tier's current name, joined for display. */
    priceTierName: z.string().nullable(),
    paymentMethod: z.string().nullable(),
    leadTimeDays: z.number().int().nullable(),
    openingBalance: z.string(),
    bouncedCheque: z.boolean(),
    bouncedChequeNote: z.string().nullable(),
    notes: z.string().nullable(),
    /**
     * MD-01 field 29 / F-15 — opening balance + POSTED invoices − receipts.
     * Never typed by hand (BR-MD-01-13); drafts contribute nothing (MD-RULE-7).
     * In the partner's own (default) `currency` only — documents in any other
     * currency are in `balances`, never added in here.
     */
    owedToUs: z.string(),
    /**
     * Wave 5D — the SUPPLIER side: what we owe them, in the partner's own
     * currency. The supplier statement's own ledger rule (CRM-04 supplier
     * view): Σ POSTED bills − Σ POSTED supplier payments − Σ debit notes past
     * posting (posted / credited / refunded). Drafts count for nothing (R9).
     * "0.00" for a partner we have never bought from. Like `owedToUs`, the
     * default-currency figure only; other currencies are in `balances`.
     */
    owedByUs: z.string(),
    /**
     * Every currency this partner has a non-zero balance in, one row each,
     * the partner's own currency first and the rest by code. Empty for a
     * partner with nothing open in any currency.
     */
    balances: PartnerCurrencyBalanceSchema.array(),
    status: PartnerStatusSchema,
    /**
     * BR-MD-01-09a / BR-MD-01-09b — the credit bar the partner screen draws,
     * decided by the SERVER because the effective limit is a server fact (the
     * partner's own, else the company Debt limit — MD-01 §9):
     *   'amber'  balance at or past 80% of the limit — a warning to a human,
     *            it blocks nothing;
     *   'red'    balance past the limit AND enforcement is not None. Setting
     *            enforcement to None suppresses the red bar and its banner
     *            even while over — the balance still shows amber, because it
     *            has reached 80% and -09a does not bend to the setting;
     *   'none'   under 80%, or no positive limit anywhere.
     */
    creditBar: z.enum(['none', 'amber', 'red']),
    /**
     * The excess the red banner names — exposure − limit; null unless the bar
     * is red. The limit is in the partner's currency; the exposure it is
     * measured against (bar and over-limit chip alike) is every currency's
     * open balance converted into the partner's currency through the base at
     * the CURRENT rate. A currency with no rate yet cannot be converted and is
     * left out rather than guessed.
     */
    creditExcess: z.string().nullable(),
    // MD-RULE-5's six fields, `createdAt` among them. rule-coverage:ignore
    ...AuthorshipSchema.shape,
})
    .strict();
const AddressFieldsSchema = {
    billingStreet: z.string().trim().max(120).optional(),
    billingCity: z.string().trim().max(60).optional(),
    billingRegion: z.string().trim().max(60).optional(),
    billingPostalCode: z.string().trim().max(20).optional(),
    billingCountry: CountrySchema.optional(),
    shippingSameAsBilling: z.boolean().optional(),
    shippingStreet: z.string().trim().max(120).optional(),
    shippingCity: z.string().trim().max(60).optional(),
    shippingRegion: z.string().trim().max(60).optional(),
    shippingPostalCode: z.string().trim().max(20).optional(),
    shippingCountry: CountrySchema.optional(),
};
export const CreatePartnerRequestSchema = z
    .object({
    name: NameSchema,
    /** At least one must end up true (BR-MD-01-05a); defaults to customer. */
    isCustomer: z.boolean().default(true),
    isSupplier: z.boolean().default(false),
    /**
     * MD-01 field 4. When set and `paymentTermId` is omitted, the group's
     * default term is applied ("used for … default terms").
     */
    partnerGroupId: z.uuid().optional(),
    taxId: z.string().trim().min(1).max(40).optional(),
    primaryContact: z.string().trim().max(120).optional(),
    phone: z.string().trim().max(30).optional(),
    email: EmailSchema.optional(),
    ...AddressFieldsSchema,
    currency: CurrencySchema.optional(),
    /**
     * MD-01 field 22. Omitted = the tenant's default-for-new-customers (or
     * -vendors) term, when one is ticked (MD-09 fields 7–8).
     */
    paymentTermId: z.uuid().optional(),
    creditLimit: MoneyStringSchema.optional(),
    creditEnforcement: CreditEnforcementSchema.optional(),
    discountPct: PctStringSchema.optional(),
    /** BR-MD-01-16: must be one of the tenant's ACTIVE tiers when picked. */
    priceTierId: z.uuid().optional(),
    paymentMethod: z.string().trim().max(40).optional(),
    leadTimeDays: z.number().int().min(0).max(365).optional(),
    openingBalance: MoneyStringSchema.optional(),
    notes: z.string().max(2000).optional(),
})
    .strict()
    .refine((value) => value.isCustomer || value.isSupplier, {
    message: 'A partner must have at least one type ticked.',
});
export const UpdatePartnerRequestSchema = z
    .object({
    name: NameSchema.optional(),
    isCustomer: z.boolean().optional(),
    isSupplier: z.boolean().optional(),
    partnerGroupId: z.uuid().nullable().optional(),
    taxId: z.string().trim().min(1).max(40).nullable().optional(),
    primaryContact: z.string().trim().max(120).nullable().optional(),
    phone: z.string().trim().max(30).nullable().optional(),
    email: EmailSchema.nullable().optional(),
    ...AddressFieldsSchema,
    currency: CurrencySchema.optional(),
    paymentTermId: z.uuid().nullable().optional(),
    creditLimit: MoneyStringSchema.nullable().optional(),
    creditEnforcement: CreditEnforcementSchema.optional(),
    discountPct: PctStringSchema.optional(),
    priceTierId: z.uuid().nullable().optional(),
    paymentMethod: z.string().trim().max(40).nullable().optional(),
    leadTimeDays: z.number().int().min(0).max(365).nullable().optional(),
    openingBalance: MoneyStringSchema.optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
/**
 * INV-F3 (bug triage Oct 2026) — a partner's BRANCHES as records: the sites
 * a customer calls by name, picked on the sales header instead of typed.
 *
 * Master data OF A PARTNER (`GET/POST/PATCH /api/partners/:id/branches…`),
 * in the smallest honest shape: on/off like every vocabulary — off = not
 * offered on a NEW document, documents already naming it keep it — MD-RULE-5's rule-coverage:ignore
 * author facts, and NO delete: a branch that was ever named is history on the
 * documents that name it. A duplicate name on one partner is refused 409
 * (`PARTNER_MESSAGES.branchNameTaken`; the unique index is the floor).
 */
export const PartnerBranchSchema = z
    .object({
    id: z.uuid(),
    partnerId: z.uuid(),
    name: z.string(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    phone: z.string().nullable(),
    contactName: z.string().nullable(),
    isActive: z.boolean(),
    ...AuthorshipSchema.shape,
})
    .strict();
export const CreatePartnerBranchRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    address: z.string().trim().max(200).optional(),
    city: z.string().trim().max(60).optional(),
    phone: z.string().trim().max(30).optional(),
    contactName: z.string().trim().max(120).optional(),
})
    .strict();
/** PATCH: absent = untouched, null = cleared (name cannot be cleared). */
export const UpdatePartnerBranchRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    address: z.string().trim().max(200).nullable().optional(),
    city: z.string().trim().max(60).nullable().optional(),
    phone: z.string().trim().max(30).nullable().optional(),
    contactName: z.string().trim().max(120).nullable().optional(),
})
    .strict();
/** 201/200 carry the record PLUS non-blocking warnings (duplicate name). */
export const PartnerWriteResponseSchema = z
    .object({
    partner: PartnerSchema,
    warnings: z.string().array(),
})
    .strict();
/**
 * CRM-04 §5c — "One record, two directions." The view, and the sign the
 * running balance carries: Customer view is +1, Supplier view is −1
 * (BR-CRM-04-04).
 *
 * §15 "A partner who is both" settles what the toggle actually switches:
 * "the two views are SEPARATE LEDGERS, not one ledger re-signed. Switching
 * shows a different opening balance, different rows and a different aging
 * profile." So `side` chooses which set of documents is read, and the sign
 * then falls out of which money column each kind lands in — see
 * `partner-statement.ts`.
 */
export const StatementSideSchema = z.enum(['customer', 'supplier']);
/**
 * CRM-04 §5f's badge table, in this build's document vocabulary.
 *
 * The spec names seven types with prototype keys; six of them are documents
 * this product posts, and each is mapped to the table it comes from:
 *
 *   `invoice`          → invoices               (spec `invoice`,     blue)
 *   `receipt`          → receipts               (spec `payment`,     green)
 *   `credit_note`      → credit_notes           (spec `credit`,      amber)
 *   `purchase_invoice` → purchase_invoices      (spec `pinv`,        blue)
 *   `payment`          → payments               (spec `paymentMade`, green)
 *   `purchase_return`  → purchase_returns       (spec `debit`,       red)
 *
 * The seventh, `po` (a purchase ORDER, grey), is deliberately absent. Owner
 * ruling R9 and BR-CRM-04-19 admit POSTED documents only, and a purchase
 * order posts no journal entry and moves no balance — it is a commitment,
 * not a debt. Putting it on a statement would show a supplier money we do
 * not yet owe them, which is the same failure a draft invoice would be.
 * Recorded here rather than silently dropped.
 *
 * PUR-04's supplier return IS the spec's debit note — `purchase-orders.
 * service.ts` says so outright ("PUR-04's supplier return / debit note").
 */
export const StatementRowKindSchema = z.enum([
    'invoice',
    'receipt',
    'credit_note',
    'purchase_invoice',
    'payment',
    'purchase_return',
]);
/**
 * CRM-04 §5e — the five aging bands, "Five, and no others".
 *
 * ── EC-19-75, A SPEC SELF-CONTRADICTION THE OWNER HAS NOT RULED ON ─────────
 *
 * The source material bands aged debt TWO ways, and both readings are
 * written down in the same document:
 *
 *   §21.8  "The statement's aged-debt breakdown uses FIVE buckets —
 *           Current · 1–30 · 31–60 · 61–90 · 90+ — and the dashboard panel
 *           is widened to match (F-14, EC-19-75)."
 *   §21.12 "The money-owed report uses the FOUR buckets Current, 1–30,
 *           31–60, and 60+ days (F-13)."
 *
 * `EC-19-75` records the clash and is still marked **To decide**: "Aged debt
 * is bucketed two different ways… One breakdown MUST win on both screens.
 * Proposed: the statement's five bands, because 61–90 is a distinction an
 * accountant chases on."
 *
 * FIVE IS BUILT HERE, and the reason is not the proposal — it is that for
 * THIS screen the spec resolves its own clash in the screen's own words.
 * CRM-04 §5e: "Five bands is what the screen renders. A four-band
 * description of receivables aging appears elsewhere in this document; where
 * the two disagree, THIS SCREEN's five bands are correct." And BR-CRM-04-09
 * is unconditional: "The aging breakdown must have exactly five bands."
 * A four-band CRM-04 would contradict a BR rule to satisfy a checklist line
 * about a different screen.
 *
 * EC-19-75's SECOND HALF IS NOW RULED. It asked whether the DASHBOARD's
 * money-owed panel widens to five to match (§21.8's "the dashboard panel is
 * widened to match") or keeps F-13's four (§21.12). **DECISION 5** (delegated
 * rulings, 26 Aug 2026): "aged-debt buckets → five everywhere. The statement
 * already resolved to five; one number must not disagree with itself one
 * click away." The dashboard's `arAging` widens to these same five bands, and
 * `widgets.tsx`'s `overdueTone` — which carried the same record — cites the
 * ruling too. F-13's four-bucket description is superseded.
 */
export const AGING_BANDS = ['current', 'd1_30', 'd31_60', 'd61_90', 'd90_plus'];
export const AgingBandSchema = z.enum(AGING_BANDS);
/**
 * THE BOUNDARIES, ONCE, BECAUSE DECISION 5 PUT THEM ON TWO SCREENS.
 *
 * Each band's INCLUSIVE upper bound in DAYS PAST DUE; `null` is the open end.
 * "Days past due" is `today − due_date`, so it is zero or negative while a
 * document is not yet due — Current is "not yet due", and a document due
 * TODAY is current, because a document due today is not late.
 *
 * ── WHY THIS EXISTS AT ALL ────────────────────────────────────────────────
 *
 * Before D5 the numbers lived in exactly one place: `bandFor` in
 * `partner-statement.ts`. D5 puts the same five bands on the financial
 * dashboard, whose panel aggregates in SQL rather than in TypeScript — the
 * two cannot literally share a function, because one of them is a
 * `FILTER (WHERE …)` clause. What they CAN share, and what actually prevents
 * the two screens disagreeing, is the NUMBERS. So the boundaries live here,
 * beside the band list they bound, and the dashboard's SQL interpolates them
 * rather than retyping 30/60/90 as a third copy.
 *
 * ── AND THE ONE THING THIS IS NOT ─────────────────────────────────────────
 *
 * `partner-statement.ts` still carries its own `bandFor`, deliberately: that
 * file is a live wave's ground and this wave is fenced out of it. The two
 * implementations are bound to this one table and pinned identical by
 * `aging-bands-one-arithmetic.spec.ts`, which fails if either drifts. When
 * the fence lifts, the whole unification is four lines — delete `bandFor`'s
 * body and call `agingBandForDaysPastDue` — and that pin is what makes doing
 * so safe rather than hopeful.
 */
export const AGING_BAND_MAX_DAYS_PAST_DUE = {
    current: 0,
    d1_30: 30,
    d31_60: 60,
    d61_90: 90,
    d90_plus: null,
};
/**
 * One document's lateness to its band. `daysPastDue` is `today − due_date`.
 *
 * The walk is over `AGING_BANDS` in order rather than a chain of `if`s, so
 * adding or moving a band is a change to the table above and to nothing else
 * — which is the property D5 needed and the four-band panel did not have.
 */
export function agingBandForDaysPastDue(daysPastDue) {
    for (const band of AGING_BANDS) {
        const max = AGING_BAND_MAX_DAYS_PAST_DUE[band];
        if (max !== null && daysPastDue <= max)
            return band;
    }
    return 'd90_plus';
}
/**
 * BR-CRM-04-11: "A band with no money must still render its card, with the
 * amount greyed." So the array is always five entries in `AGING_BANDS`
 * order, zeros included — a band is never omitted for being empty, and the
 * screen therefore cannot accidentally draw four.
 */
export const StatementAgingSchema = z
    .object({ band: AgingBandSchema, amount: z.string() })
    .strict();
/**
 * CRM-04 §5d — the five summary cards, computed by the SERVER over the whole
 * ledger.
 *
 * BR-CRM-04-13 is the rule that decides where these live: "The running
 * balance, both totals and the closing balance must be computed over the
 * WHOLE LEDGER before any search filter is applied." A screen that added up
 * the rows it was sent would report the current PAGE — the same defect
 * `leads.service.ts stats()` exists to prevent, and PAGINATION.md §3 step 10
 * names ("a screen may derive something from the WHOLE list without saying
 * so"). These five are `GET /api/crm/leads/stats`'s precedent applied to a
 * statement.
 */
export const StatementSummarySchema = z
    .object({
    /** BR-CRM-04-21: the position at the From date — everything posted before it. */
    openingBalance: z.string(),
    /** BR-CRM-04-05: UNSIGNED. The view's sign is not applied to either total. */
    totalDebits: z.string(),
    totalCredits: z.string(),
    /** BR-CRM-04-06: the running balance after the final row, never a second sum. */
    closingBalance: z.string(),
    /** BR-CRM-04-07: every aging band EXCEPT Current. */
    overdueAmount: z.string(),
})
    .strict();
/** One row of the CRM-04 statement — POSTED documents only (R9, MD-RULE-7). */
export const StatementRowSchema = z
    .object({
    /**
     * BR-CRM-04-15: "A document number must link to that document." The id is
     * what the link navigates with — `kind` says which editor route it opens.
     */
    id: z.uuid(),
    date: IsoDateSchema,
    kind: StatementRowKindSchema,
    code: z.string(),
    /**
     * CRM-04 §5f column 4, "Description / reference — free text from the
     * document", and the second half of what the search box matches
     * (§5a field 6: "the document number OR the reference").
     */
    reference: z.string().nullable(),
    /** Invoice grand totals debit the customer; receipts AND credit notes credit them (SAL-05 §12: "the customer owes less"). */
    debit: z.string(),
    credit: z.string(),
    /** Running balance after this row. */
    balance: z.string(),
    /**
     * EC-19-10, on a statement: the rate THIS document froze, and the row
     * converted at it. A statement re-derived from today's table would say a
     * different thing every morning about the same unchanged invoice, which
     * is the exact failure "changing a rate today must never change a
     * document from last month" forbids. Null when the document predates the
     * freeze and no rate can be reconstructed — the honest answer, and
     * distinguishable from zero.
     */
    exchangeRate: z.string().nullable(),
    rateEffectiveOn: IsoDateSchema.nullable(),
    debitBase: z.string().nullable(),
    creditBase: z.string().nullable(),
})
    .strict();
export const PartnerStatementSchema = z
    .object({
    partnerId: z.uuid(),
    /**
     * The currency this statement is IN — every debit/credit/balance is in
     * it. The partner's own currency unless the caller asked for another
     * (`currency` query); documents in other currencies are on their own
     * statements, never added in here.
     */
    currency: z.string(),
    /**
     * Every currency the partner has documents in on this side, plus their
     * own; the partner's own first, the rest by code. The screen's currency
     * switcher is drawn from it (a one-entry list draws no switcher).
     */
    currencies: z.string().array(),
    /**
     * BR-CRM-04-02: "Selecting a partner must also set the view: Customer
     * view when they are a customer, Supplier view otherwise." The server
     * resolves it, so a caller that names no side gets the right ledger and
     * the printed statement cannot disagree with the screen.
     */
    side: StatementSideSchema,
    /**
     * BR-CRM-04-03: "The view toggle must appear only when the partner is
     * both a customer and a supplier." A boolean rather than the screen
     * re-deriving `isCustomer && isSupplier`, so the toggle and the ledger
     * beneath it are answering the same question from the same read.
     */
    canToggle: z.boolean(),
    /** BR-CRM-04-18's window, as resolved — §5d's two card sub-captions. */
    from: IsoDateSchema,
    to: IsoDateSchema,
    /** §5d's five cards — see StatementSummarySchema. */
    summary: StatementSummarySchema,
    /** §5e's five bands, always five, in order (BR-CRM-04-09/-11). */
    aging: StatementAgingSchema.array(),
    /**
     * One PAGE of the ledger (NFR-42). The figures above are not derived
     * from it and cannot be — see StatementSummarySchema.
     */
    rows: StatementRowSchema.array(),
    /** What the company reports in (FIN-05's base). */
    baseCurrency: z.string(),
    /**
     * The closing balance in the base currency, each row converted at ITS OWN
     * frozen rate and the results added — never the closing balance converted
     * once at today's rate, which is a different and wrong number whenever the
     * rate moved during the period.
     *
     * Null when any posted row on the statement has no frozen rate: a total
     * that silently omits rows is worse than no total, and GEN-115's
     * disclosure has nothing to disclose if the figure is a guess.
     */
    closingBalanceBase: z.string().nullable(),
    /**
     * GEN-115: "Any screen that converts money between currencies must show a
     * one-line disclosure directly above the numbers, naming the target
     * currency and the date of the rates used." Null when no conversion
     * happened (GEN-116) — a base-currency customer sees nothing.
     */
    conversionNote: z.string().nullable(),
    /**
     * F-27's guard, visible. One sentence per currency found on this
     * partner's documents that is NOT the statement's `currency` — those rows
     * are excluded from every figure on the screen rather than added into it,
     * and are on that currency's own statement (see `currencies`). The
     * exclusion is never silent — the same exclude-then-name shape
     * `Consolidation.toTarget` uses for a rate-less currency.
     */
    excludedCurrencies: z.string().array(),
    /**
     * NFR-42's meta, on the LEDGER only. `total` is the number of rows the
     * period and the search leave — never the number the summary was
     * computed from, which is the whole ledger and is not a count anybody
     * prints. See `partner-statement.ts` for the order the two happen in.
     */
    ...PageMetaShape,
})
    .strict();
/**
 * Wave 1B — what THIS customer has bought and returned of ONE item, read off
 * POSTED sales invoices and POSTED credit notes (R9: a draft sells nothing
 * and returns nothing). The stand-alone sales return (RET-02) reads it when
 * a line's item is picked: `lastPrice` fills the unit price ahead of the
 * item's catalogue price (the customer is credited what they paid, not
 * today's list price), and `qtySold − qtyReturned` is the figure the editor
 * warns against when a return names more than the customer ever bought — a
 * hint, never a block, because a stand-alone return has no invoice to cap
 * against. Tenant-scoped like every read: another tenant's sales are not a
 * figure here, they are a 404 on the partner.
 */
export const PartnerItemHistorySchema = z
    .object({
    /** The unit price on the customer's most recent POSTED invoice line for the item; null when never sold. */
    lastPrice: z.string().nullable(),
    /** That invoice's currency (the price is meaningless without it); null with `lastPrice`. */
    lastPriceCurrency: z.string().nullable(),
    /** That invoice's date; null with `lastPrice`. */
    lastPriceAt: IsoDateSchema.nullable(),
    /** Σ qty on POSTED invoice lines for the item, in the line's typed unit. */
    qtySold: z.string(),
    /** Σ qty on POSTED credit-note lines for the item (invoice-backed and stand-alone alike). */
    qtyReturned: z.string(),
})
    .strict();
/**
 * Wave 5D — the SUPPLIER-side mirror of `PartnerItemHistorySchema`: what we
 * have bought from THIS supplier of ONE item and sent back, read off POSTED
 * supplier bills and POSTED debit notes (R9). The DIRECT purchase return
 * reads it when a line's item is picked: `lastPrice` fills the unit price
 * ahead of the item's cost price (the supplier credits what they charged,
 * not today's catalogue cost), and `qtyBought − qtyReturned` is the figure
 * the editor WARNS against when more goes back than was ever bought — a
 * hint, never a block, because a direct return has no bill to cap against.
 */
export const PartnerSupplierItemHistorySchema = z
    .object({
    /** The unit price on the supplier's most recent POSTED bill line for the item; null when never bought. */
    lastPrice: z.string().nullable(),
    /** That bill's currency; null with `lastPrice`. */
    lastPriceCurrency: z.string().nullable(),
    /** That bill's date; null with `lastPrice`. */
    lastPriceAt: IsoDateSchema.nullable(),
    /** Σ qty on POSTED bill lines for the item, in the stock unit. */
    qtyBought: z.string(),
    /** Σ qty on POSTED / credited / refunded debit-note lines for the item (bill-linked and direct alike). */
    qtyReturned: z.string(),
})
    .strict();
const partnerParams = z.object({ partnerId: z.uuid() }).strict();
const branchParams = z.object({ partnerId: z.uuid(), branchId: z.uuid() }).strict();
export const partnersContract = c.router({
    /**
     * INV-F3 — the partner's branches. Nested under the partner because a
     * branch is a fact about ONE partner and nothing else reads them except
     * the sales header's picker for that partner. A vocabulary of a record,
     * unpaged like the manufacturers list: the picker shows them all.
     */
    listBranches: {
        method: 'GET',
        path: '/api/partners/:partnerId/branches',
        pathParams: partnerParams,
        query: z
            .object({
            /** The partner form's switch; the sales picker asks without it. */
            includeInactive: z.enum(['true', 'false']).optional(),
        })
            .strict(),
        responses: {
            200: z.object({ branches: PartnerBranchSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The partner’s branches (crm.view), name order; active only unless asked.',
    },
    createBranch: {
        method: 'POST',
        path: '/api/partners/:partnerId/branches',
        pathParams: partnerParams,
        body: CreatePartnerBranchRequestSchema,
        responses: {
            201: PartnerBranchSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'A new branch on the partner (crm.edit); a duplicate name on that partner is refused.',
    },
    updateBranch: {
        method: 'PATCH',
        path: '/api/partners/:partnerId/branches/:branchId',
        pathParams: branchParams,
        body: UpdatePartnerBranchRequestSchema,
        responses: {
            200: PartnerBranchSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a branch (crm.edit) — the name reaches every document that points at it by id.',
    },
    deactivateBranch: {
        method: 'POST',
        path: '/api/partners/:partnerId/branches/:branchId/deactivate',
        pathParams: branchParams,
        body: z.object({}).strict().optional(),
        responses: { 200: PartnerBranchSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'Switch a branch off (crm.edit): not offered on a new document; documents already naming it keep it.',
    },
    reactivateBranch: {
        method: 'POST',
        path: '/api/partners/:partnerId/branches/:branchId/reactivate',
        pathParams: branchParams,
        body: z.object({}).strict().optional(),
        responses: { 200: PartnerBranchSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'Switch a branch back on (crm.edit).',
    },
    list: {
        method: 'GET',
        path: '/api/partners',
        query: pagedQuery({
            /** Matches name or code (MD-01 §6: search on code and name). */
            q: z.string().optional(),
            type: z.enum(['customer', 'supplier']).optional(),
            /** MD-01 field 4's "used for reporting": filter to one group. */
            group: z.uuid().optional(),
            /** BR-MD-01-07: pickers must exclude the switched-off by default. */
            includeInactive: z.enum(['true', 'false']).optional(),
            /**
             * GEN-50's second read: the partners a form already holds. Ignores
             * every other filter — a document keeps showing its customer after
             * that customer is switched off. See `pagination.ts`.
             */
            ids: PickerIdsSchema.optional(),
        }),
        responses: {
            200: pagedResponse('partners', PartnerSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The partner list (crm.view), FILTERED IN SQL AND PAGED (NFR-42) and the partner typeahead (GEN-50): `q` matches name or the PRINTED code, `ids` resolves what a form already holds. Balances count posted documents only.',
    },
    get: {
        method: 'GET',
        path: '/api/partners/:partnerId',
        pathParams: partnerParams,
        responses: {
            200: PartnerSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One partner (crm.view).',
    },
    create: {
        method: 'POST',
        path: '/api/partners',
        body: CreatePartnerRequestSchema,
        responses: {
            201: PartnerWriteResponseSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New partner (crm.create). Duplicate tax id refused (BR-MD-01-03); duplicate name warns and saves.',
    },
    update: {
        method: 'PATCH',
        path: '/api/partners/:partnerId',
        pathParams: partnerParams,
        body: UpdatePartnerRequestSchema,
        responses: {
            200: PartnerWriteResponseSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a partner (crm.edit). Currency locked once documents exist (BR-MD-01-05); types cannot be unticked while documents of that kind exist (BR-MD-01-05b).',
    },
    deactivate: {
        method: 'POST',
        path: '/api/partners/:partnerId/deactivate',
        pathParams: partnerParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PartnerSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Switch off (crm.edit). Refused while an unpaid POSTED invoice exists (BR-MD-01-12); drafts never block.',
    },
    reactivate: {
        method: 'POST',
        path: '/api/partners/:partnerId/reactivate',
        pathParams: partnerParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PartnerSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch back on (crm.edit).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/partners/:partnerId',
        pathParams: partnerParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete (crm.delete) — only a partner used NOWHERE (MD-RULE-1/ERR-MD-01-05); the RESTRICT FKs are the floor.',
    },
    statement: {
        method: 'GET',
        path: '/api/partners/:partnerId/statement',
        pathParams: partnerParams,
        query: pagedQuery({
            /**
             * CRM-04 §5a fields 3 and 4, and the prototype defect the build must
             * NOT reproduce: "**The period does not filter anything.** Both
             * inputs are read into the opening and closing captions, and the
             * ledger renders every row the partner has, whatever the dates say."
             * BR-CRM-04-18 is the rule; these two are how it is asked for.
             *
             * Both optional: the server resolves the tenant's own default window
             * when they are absent, so the printed and emailed statements get a
             * period without every caller having to invent one.
             */
            from: IsoDateSchema.optional(),
            to: IsoDateSchema.optional(),
            /**
             * §5c's view. Absent means BR-CRM-04-02's answer — Customer when
             * they are a customer, Supplier otherwise.
             */
            side: StatementSideSchema.optional(),
            /**
             * §5a field 6: "Filters the ledger rows. Matches the **document
             * number or the reference**, ignoring capital letters."
             *
             * §5g is the half that is easy to build wrong, and it is a rule
             * (BR-CRM-04-13): the search filters ROWS ONLY. Every figure in
             * `summary` is computed before it runs, so "searching for one invoice
             * shows that row WITH ITS TRUE RUNNING BALANCE in the context of the
             * whole statement". A filtered view is a window onto the account,
             * not a different account.
             */
            q: z.string().optional(),
            /**
             * Which currency's statement (owner decision, 30 Oct 2026: balances
             * are per currency). Absent = the partner's own currency.
             */
            currency: CurrencySchema.optional(),
        }),
        responses: {
            200: PartnerStatementSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The partner statement (crm.view) — POSTED documents only, ruling R9. The LEDGER is paged (NFR-42); the five summary cards and the five aging bands are computed over the whole ledger and never from the page (BR-CRM-04-13).',
    },
    /**
     * CRM-04 §5 "Send Statement" — "the one outward-facing action", and the
     * prototype's defect list is the specification for this route: the
     * prototype "sets a confirmation flag and sends no email", and the fix is
     * "Send it, and record that it was sent."
     *
     * So it QUEUES rather than sends: a statement run must not make the person
     * who clicked wait on a provider, and a provider outage must not turn a
     * green confirmation into a red one. The row in `email_outbox` IS the
     * record that it was sent, and its `failed` status is the record that it
     * was not.
     *
     * Permission is `crm.view`, from §5's own table — "Anyone with crm.view —
     * the shipped Accountant role has it". Sending a statement is showing the
     * customer what they already owe, not changing anything.
     */
    sendStatement: {
        method: 'POST',
        path: '/api/partners/:partnerId/statement/send',
        pathParams: partnerParams,
        body: z
            .object({
            /** Which currency's statement to send; absent = the partner's own. */
            currency: CurrencySchema.optional(),
        })
            .strict()
            .optional(),
        responses: {
            /** The address, so the screen can say "Statement emailed to …". */
            200: z.object({ emailedTo: z.string() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            /** No address on file — nothing to send to (ERR-shaped, not a crash). */
            409: MessageSchema,
        },
        summary: 'CRM-04 "Send Statement" (crm.view): queues the statement email to the partner and records the attempt.',
    },
    /**
     * Wave 1B — the customer's history with one item, for the stand-alone
     * sales return (RET-02). Under `sales.view` rather than `crm.view`: it
     * answers a SALES question (what did we sell them, at what price) and the
     * screen that asks it is a sales document, so the AP Clerk who may read
     * the partner list still may not read the customer's prices.
     */
    itemHistory: {
        method: 'GET',
        path: '/api/partners/:partnerId/item-history',
        pathParams: partnerParams,
        query: z.object({ itemId: z.uuid() }).strict(),
        responses: {
            200: PartnerItemHistorySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: "The customer's last POSTED sale price for an item and the quantities sold and returned (sales.view). POSTED documents only (R9); another tenant's partner is a 404.",
    },
    /**
     * Wave 5D — the supplier's history with one item, for the DIRECT purchase
     * return. Under `purchasing.view` for the mirror reason `itemHistory` is
     * under `sales.view`: it answers what we BOUGHT from them and at what
     * price, which is the purchasing area's question.
     */
    supplierItemHistory: {
        method: 'GET',
        path: '/api/partners/:partnerId/supplier-item-history',
        pathParams: partnerParams,
        query: z.object({ itemId: z.uuid() }).strict(),
        responses: {
            200: PartnerSupplierItemHistorySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: "The supplier's last POSTED bill price for an item and the quantities bought and sent back (purchasing.view). POSTED documents only (R9); another tenant's partner is a 404.",
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=partners.js.map